/**
 * Feature switches (D14): which parts of Bar OS each organisation has.
 *
 * Venox turns features and sub-features on and off per organisation, from
 * the Platform screen. A feature that is off is hidden entirely — gone from
 * the menu and the screens, refused by the API, skipped by the scheduled
 * jobs — and its data is left exactly as it was, so switching it back on
 * restores everything.
 *
 * This file is the catalogue: every switchable feature, what it sits under,
 * what it needs, and the presets a new organisation starts from. The API
 * maps routes onto these ids (packages/api/src/features.ts); the web app
 * maps screens and controls onto them. A feature that is not in this list
 * cannot be switched off, which is deliberate for the few things every
 * organisation must have (`ALWAYS_ON`).
 *
 * Per organisation only, for now. The stored shape is a plain map so a
 * per-venue override can be laid over it later without a migration.
 */
/** The menu-level areas the features are shown under on the Platform screen. */
export const FEATURE_AREAS = [
    { id: "development", label: "Development" },
    { id: "service", label: "Service" },
    { id: "stock", label: "Stock" },
    { id: "reports", label: "Reports and integrations" },
    { id: "admin", label: "Admin" },
];
/**
 * Every switchable feature. Order is the Platform screen's order; a
 * sub-feature follows its parent.
 */
export const FEATURES = [
    // ------------------------------------------------------------ development
    { id: "chat", label: "Development chat", area: "development", description: "The AI chat that develops drinks against the library. Costs AI spend." },
    { id: "books", label: "Recipe books", area: "development", parent: "chat", description: "Uploading reference books for the chat to search." },
    { id: "wheel", label: "Flavour wheel", area: "development", description: "Ingredient aroma profiles and pairings." },
    { id: "menu", label: "Menu engineering", area: "development", description: "Prices, margins and what sells, per venue." },
    { id: "sharing", label: "Share a drink", area: "development", description: "Copy a spec out as a public caption." },
    { id: "batching", label: "Batch calculator", area: "development", description: "Scale a build to a batch or a serve size." },
    // ---------------------------------------------------------------- service
    { id: "whattail", label: "WhatTail", area: "service", requires: ["wheel"], description: "A short questionnaire a bartender runs with a guest to land on three drinks from tonight's list. Uses the flavour data; no AI spend." },
    { id: "prep", label: "Prep lists", area: "service", description: "Tabs of prep tasks for each service." },
    { id: "cashup", label: "Cash-up", area: "service", description: "Counting the till and signing the night off." },
    { id: "queue", label: "Door queue", area: "service", description: "Names and numbers for a full bar, seated by a tap." },
    { id: "queueTexts", label: "Queue texts", area: "service", parent: "queue", description: "Texting guests that they're on the list and that their table is ready. Costs per text." },
    { id: "guestPage", label: "Guest page", area: "service", parent: "queue", description: "The public page a queue text links to: place in the queue, tonight's drinks, an optional form." },
    { id: "guests", label: "Guest list", area: "service", parent: "queue", description: "Guest records, preferences and visit history, for managers." },
    { id: "training", label: "Training", area: "service", description: "Modules the bar writes about how it runs, worked through and checked, with each person's progress. No AI spend." },
    // ------------------------------------------------------------------ stock
    { id: "stocktake", label: "Stocktake", area: "stock", description: "Counting stock and finalising a count." },
    { id: "wastage", label: "Wastage", area: "stock", description: "Logging and approving what was lost." },
    { id: "suppliers", label: "Suppliers", area: "stock", description: "Suppliers, their items and cut-offs." },
    { id: "orders", label: "Orders", area: "stock", requires: ["suppliers"], description: "Raising, approving and sending supplier orders." },
    { id: "expenses", label: "Expenses", area: "stock", description: "Recording supplier invoices and other spend." },
    { id: "slipScan", label: "Invoice scanning", area: "stock", parent: "expenses", description: "Photographing an invoice and reading it automatically." },
    { id: "dext", label: "Dext", area: "stock", parent: "expenses", description: "Sending receipts on to the bar's Dext account." },
    // --------------------------------------------------- reports, integrations
    { id: "square", label: "Square till", area: "reports", description: "Connecting Square: what the till took, synced every 15 minutes, and the Sales screen." },
    { id: "notices", label: "Dashboard notices", area: "reports", description: "Nightly and hourly checks that raise notices on the dashboard." },
    { id: "orderReminders", label: "Order cut-off reminders", area: "reports", parent: "notices", requires: ["orders"], description: "A notice before a supplier's cut-off when nothing has been ordered." },
    { id: "stockNotices", label: "Stock and variance notices", area: "reports", parent: "notices", requires: ["stocktake"], description: "Notices for counts that are due and variances worth a look." },
    // ------------------------------------------------------------------ admin
    { id: "tablets", label: "Tablets and PINs", area: "admin", description: "Enrolling a venue tablet that staff unlock with their own PIN." },
];
export const FEATURE_IDS = FEATURES.map((f) => f.id);
const BY_ID = new Map(FEATURES.map((f) => [f.id, f]));
export function featureDef(id) {
    return BY_ID.get(id);
}
export function isFeatureId(value) {
    return typeof value === "string" && BY_ID.has(value);
}
/**
 * What no switch reaches: the things every organisation has, listed so the
 * Platform screen can say so rather than leave someone looking for them.
 */
export const ALWAYS_ON = [
    "Dashboard",
    "Recipes and the library",
    "Organisation: members, venues, settings",
    "Sign-in and two-step sign-in",
    "Activity log",
    "Data export",
];
/** Only known ids with boolean values; anything else in the stored map is ignored. */
export function settledSwitches(raw) {
    if (typeof raw !== "object" || raw === null || Array.isArray(raw))
        return {};
    const out = {};
    for (const [k, v] of Object.entries(raw)) {
        if (isFeatureId(k) && typeof v === "boolean")
            out[k] = v;
    }
    return out;
}
/**
 * The features an organisation actually has: switched on (or never
 * switched), with its parent on, and everything it requires on. Resolved
 * to a fixed point so a chain of requirements settles whatever the order.
 */
export function resolveFeatures(switches) {
    const on = new Set(FEATURE_IDS.filter((id) => switches[id] !== false));
    for (let changed = true; changed;) {
        changed = false;
        for (const id of [...on]) {
            const def = featureDef(id);
            const needs = [...(def.parent ? [def.parent] : []), ...(def.requires ?? [])];
            if (needs.some((n) => !on.has(n))) {
                on.delete(id);
                changed = true;
            }
        }
    }
    return on;
}
/** Why a feature that is switched on still is not available: the parent or requirement that is off. */
export function blockedBy(id, enabled) {
    const def = featureDef(id);
    return [...(def.parent ? [def.parent] : []), ...(def.requires ?? [])].filter((n) => !enabled.has(n));
}
/** Validate a body of switches from the Platform screen: known ids, booleans. */
export function validateSwitches(raw) {
    if (typeof raw !== "object" || raw === null || Array.isArray(raw))
        return { ok: false, error: "features must be an object of id → true/false" };
    for (const [k, v] of Object.entries(raw)) {
        if (!isFeatureId(k))
            return { ok: false, error: `No feature called "${k}"` };
        if (typeof v !== "boolean")
            return { ok: false, error: `features.${k} must be true or false` };
    }
    return { ok: true, value: raw };
}
// ---------------------------------------------------------------- presets
/**
 * What a new organisation starts with, chosen by Venox when creating it.
 * A preset writes an explicit switch for every feature, so later additions
 * to the catalogue do not slip silently into a Starter organisation's menu.
 * The presets are the shape the plan tiers will take (#82).
 */
export const FEATURE_PRESETS = [
    {
        id: "starter",
        label: "Starter",
        description: "Run the bar: prep, stocktake, wastage, cash-up and the flavour wheel.",
        features: ["wheel", "prep", "cashup", "stocktake", "wastage"],
    },
    {
        id: "standard",
        label: "Standard",
        description: "Starter, plus WhatTail, training, ordering, expenses, menu engineering, Square, notices and tablets.",
        features: [
            "wheel", "whattail", "prep", "cashup", "stocktake", "wastage", "training",
            "menu", "sharing", "batching", "suppliers", "orders", "expenses", "slipScan",
            "square", "notices", "orderReminders", "stockNotices", "tablets",
        ],
    },
    {
        id: "full",
        label: "Full",
        description: "Everything, including the development chat, the door queue and guests.",
        features: FEATURE_IDS,
    },
];
export function isPresetId(value) {
    return FEATURE_PRESETS.some((p) => p.id === value);
}
/** A preset as stored switches: every feature explicitly on or off. */
export function presetSwitches(id) {
    const preset = FEATURE_PRESETS.find((p) => p.id === id);
    const on = new Set(preset.features);
    return Object.fromEntries(FEATURE_IDS.map((f) => [f, on.has(f)]));
}
/** Which preset these switches match exactly, if any — so the screen can say "Standard" rather than "custom". */
export function matchingPreset(switches) {
    const enabled = resolveFeatures(switches);
    for (const p of FEATURE_PRESETS) {
        const want = resolveFeatures(presetSwitches(p.id));
        if (want.size === enabled.size && [...want].every((f) => enabled.has(f)))
            return p.id;
    }
    return null;
}
/** The 403 an API refusal starts with, so a screen can tell it from any other. */
export const FEATURE_OFF_PREFIX = "Not part of this organisation's Bar OS:";
//# sourceMappingURL=features.js.map